import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import useCaseContent from "../useCaseContent.json";
import styles from "./UseCasePage.module.css";
import { getMicrosoftStoreUrl, trackMicrosoftStoreClick } from "../utils/microsoftStore";

type UseCaseSlug = keyof typeof useCaseContent;
type ContentLanguage = "en" | "ko" | "ja";

const CONTENT_LANGUAGES = new Set<ContentLanguage>(["en", "ko", "ja"]);
const SHORTS_VIDEO_ID = "LKPPmixLoL8";
const MARKETING_VIDEO_URL =
  "https://customer-unwg1qdz6bidl9iw.cloudflarestream.com/10726afee5f3590afc032d7419115a4e/iframe?controls=true";

const UseCasePage: React.FC<{
  slug: UseCaseSlug;
  language?: ContentLanguage;
  initialStoreUrl?: string;
}> = ({ slug, language, initialStoreUrl }) => {
  const { lang = "en" } = useParams<{ lang: string }>();
  const pageLanguage = language ?? lang;

  const contentLanguage = pageLanguage as ContentLanguage;
  const otherSlug: UseCaseSlug =
    slug === "ai-shorts-maker" ? "marketing-video-maker" : "ai-shorts-maker";
  const storePlacement = slug === "ai-shorts-maker"
    ? "ai_shorts_page" as const
    : "marketing_video_page" as const;
  const [storeUrl, setStoreUrl] = useState(
    initialStoreUrl ?? getMicrosoftStoreUrl(storePlacement)
  );

  useEffect(() => {
    if (initialStoreUrl) {
      setStoreUrl(getMicrosoftStoreUrl(storePlacement));
    }
  }, [initialStoreUrl, storePlacement]);

  if (!CONTENT_LANGUAGES.has(contentLanguage)) {
    return <Navigate to={`/en/${slug}`} replace />;
  }

  const content = useCaseContent[slug][contentLanguage];

  return (
    <article className={styles.page} lang={contentLanguage}>
      <header className={styles.hero}>
        <p className={styles.eyebrow}>{content.eyebrow}</p>
        <h1>{content.title}</h1>
        <p className={styles.answer}>{content.answer}</p>
      </header>

      <section className={styles.section} aria-labelledby="use-case-audience">
        <h2 id="use-case-audience">{content.audienceTitle}</h2>
        <ul className={styles.factList}>
          {content.audience.map((item) => <li key={item}>{item}</li>)}
        </ul>
      </section>

      <section className={styles.section} aria-labelledby="use-case-steps">
        <h2 id="use-case-steps">{content.stepsTitle}</h2>
        <ol className={styles.steps}>
          {content.steps.map(([title, body], index) => (
            <li key={title}>
              <span className={styles.stepNumber}>{String(index + 1).padStart(2, "0")}</span>
              <div><h3>{title}</h3><p>{body}</p></div>
            </li>
          ))}
        </ol>
      </section>

      <section className={styles.section} aria-labelledby="use-case-example">
        <h2 id="use-case-example">{content.exampleTitle}</h2>
        <p>{content.exampleText}</p>
        <div className={styles.videoFrame}>
          <iframe
            src={slug === "ai-shorts-maker"
              ? `https://www.youtube.com/embed/${SHORTS_VIDEO_ID}`
              : MARKETING_VIDEO_URL}
            title={content.exampleTitle}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </section>

      <section className={styles.limit} aria-labelledby="use-case-limits">
        <h2 id="use-case-limits">{content.limitsTitle}</h2>
        <p>{content.limits}</p>
      </section>

      <nav className={styles.related} aria-labelledby="use-case-related">
        <h2 id="use-case-related">{content.relatedTitle}</h2>
        <div className={styles.linkGrid}>
          <Link to={`/${contentLanguage}/pricing`}>{content.pricing}</Link>
          <Link to={`/${contentLanguage}/how-to-use`}>{content.howTo}</Link>
          <Link to={`/${contentLanguage}/sample-videos`}>{content.samples}</Link>
          <a
            href={storeUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackMicrosoftStoreClick(storePlacement)}
          >
            {content.download}
          </a>
          <Link to={`/${contentLanguage}/${otherSlug}`}>{content.other}</Link>
        </div>
      </nav>
    </article>
  );
};

export default UseCasePage;
